import { SurfaceCard } from "@bednbody/ui/components/surface-card";
// A therapist's six service switches (0311; Luigi 2026-10-03, Leon 2026-10-09).
// Each switch is a group of qualifications, flipped through
// set_therapist_service_switch, which uses the existing qualification
// functions — Superadmin authority, audited. Premium Therapeutic follows
// Ventosa AND Hotstone; it has no switch of its own.

import { Label } from "@bednbody/ui/components/label";
import { Switch } from "@bednbody/ui/components/switch";
import { toast } from "sonner";
import { type ServiceSwitch, useServiceSwitches, useSetServiceSwitch } from "./staff-detail.hooks";

const SWITCHES: readonly { key: ServiceSwitch; label: string; hint?: string }[] = [
  { key: "full_body", label: "Full Body", hint: "Also Couple, Dry, Hand, Head and Foot Reflex" },
  { key: "deep_tissue", label: "Deep Tissue" },
  { key: "pregnancy", label: "Pregnancy" },
  { key: "kiddie", label: "Kiddie" },
  { key: "ventosa", label: "Ventosa", hint: "Needs the Ventosa Set switched on in her session" },
  {
    key: "hot_stone",
    label: "Hotstone",
    hint: "Needs the Hot Stone Set switched on in her session",
  },
];

export function StaffServiceSwitches({
  therapistId,
  canEdit,
}: {
  therapistId: string;
  canEdit: boolean;
}) {
  const switches = useServiceSwitches(therapistId);
  const set = useSetServiceSwitch(therapistId);
  const on = switches.data ?? {};
  // Read, not inferred: `premium_both` is the therapist's ACTUAL qualification
  // for Premium Therapeutic (0311's derived group). Recomputing it from the two
  // switches here would show "on" whenever they are both on, even if the grant
  // had failed — the one case worth seeing.
  const premium = on.premium_both === true;

  return (
    <SurfaceCard render={<section />} aria-labelledby="service-switches" className="space-y-3">
      <div>
        <h3 id="service-switches" className="text-sm font-semibold text-foreground">
          Services she can do
        </h3>
        <p className="text-xs text-muted-foreground">
          {canEdit
            ? "Only a Super Admin can change these."
            : "Read-only. A Super Admin changes these."}
        </p>
      </div>
      {switches.isError ? (
        <p className="text-sm text-destructive">Couldn't load her services.</p>
      ) : (
        <ul className="space-y-3">
          {SWITCHES.map((s) => (
            <li key={s.key} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor={`switch-${s.key}`}>{s.label}</Label>
                {s.hint ? <p className="text-xs text-muted-foreground">{s.hint}</p> : null}
              </div>
              <Switch
                id={`switch-${s.key}`}
                checked={on[s.key] === true}
                disabled={!canEdit || switches.isLoading || set.isPending}
                onCheckedChange={(checked) =>
                  set.mutate(
                    { key: s.key, on: checked },
                    {
                      onError: (e) =>
                        toast.error(e instanceof Error ? e.message : "Couldn't save."),
                    },
                  )
                }
              />
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Premium Therapeutic: {premium ? "on" : "off"} — on when Ventosa and Hotstone are both on.
      </p>
    </SurfaceCard>
  );
}
